/**
 * @file ProgramsAndAdjustmentsHub.jsx
 * @module ui/src/components/admin/ProgramsAndAdjustmentsHub.jsx
 * @description
 *   C-65 — Programs & Adjustments Hub.
 *   EXECUTE → Programs & Adjustments.
 *   Route: /admin/execute/adjustments (existing — replaces AdjustmentsHub.jsx)
 *
 *   S924 — Supersedes AdjustmentsHub.jsx (S904 P0 Draw Authorizations only).
 *   Absorbs draw authorization section verbatim from AdjustmentsHub.jsx.
 *   Adds SPIFF Programs section wired to new spiff_plan_router.py.
 *   S925 — Adds ContestWorkspace (C-67). Contest section replaces ComingSoonSection.
 *   Non-Cash, Clawback sections remain stubbed — S925+.
 *
 *   Architecture (AD-76, S901 LOCKED):
 *   - Work-first, not plan-first. Plan is a FILTER, never the entry point.
 *   - CA-2 approval stays in DESIGN → Approvals for design-time plan artifacts.
 *   - This hub is for RUNTIME operational instruments only.
 *
 *   UI directives (S924):
 *   D-1: Tables → scrollable + sortable + searchable + clickable drill-down
 *   D-2: Long screens → collapsible sections
 *
 *   CSS: index.css + tokens.css. All colours via rgb(var(--color-*)).
 *
 * @author data-fit LLC · DecisionFlow · S925
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SpiffProgramWorkspace from './SpiffProgramWorkspace';
import ContestWorkspace from './ContestWorkspace';
import BonusPoolWorkspace from './BonusPoolWorkspace';
import NonCashWorkspace from './NonCashWorkspace';
import ClawbackWorkspace from './ClawbackWorkspace';

const API_BASE = '/api/v1';
const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INSTRUMENT_TYPES = [
  { key: 'all',        label: 'All' },
  { key: 'draw',       label: 'Draw Authorizations' },
  { key: 'spiff',      label: 'SPIFF Programs' },
  { key: 'contest',    label: 'Contests' },
  { key: 'bonus_pool', label: 'Bonus Pools' },
  { key: 'non_cash',   label: 'Non-Cash Awards' },
  { key: 'clawback',   label: 'Clawback Actions' },
];

const STATUS_FILTERS = [
  { key: 'all',              label: 'All statuses' },
  { key: 'draft',            label: 'Draft' },
  { key: 'pending_approval', label: 'Awaiting approval' },
  { key: 'approved',         label: 'Approved' },
  { key: 'active',           label: 'Active' },
];

const AUTH_STATUS_CONFIG = {
  pending: { label: 'Pending approval', color: 'rgb(var(--color-warning, 202 138 4))' },
  active:  { label: 'Active',           color: 'rgb(var(--color-success, 22 163 74))' },
  revoked: { label: 'Revoked',          color: 'rgb(var(--color-danger, 220 38 38))' },
};

const SPIFF_STATUS_BADGE = {
  draft:            { cls: 'modifier-manager-badge--not-created', label: 'Draft' },
  pending_approval: { cls: 'modifier-manager-badge--warning',     label: 'Awaiting Approval' },
  approved:         { cls: 'modifier-manager-badge--complete',    label: 'Approved' },
  superseded:       { cls: 'modifier-manager-badge--readonly',    label: 'Superseded' },
};

const CONTEST_BADGE = {
  draft:   'modifier-manager-badge--not-created',
  open:    'modifier-manager-badge--warning',
  active:  'modifier-manager-badge--complete',
  closed:  'modifier-manager-badge--readonly',
  awarded: 'modifier-manager-badge--complete',
};

const CONTEST_STATUS_LABEL = {
  draft: 'Draft', open: 'Open', active: 'Active', closed: 'Closed', awarded: 'Awarded',
};

const CONTEST_RANK_METRIC_LABELS = {
  attainment_pct: 'Attainment %',
  credit_total:   'Credit Total',
  margin_total:   'Margin Total',
};

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function SummaryCard({ label, value, color, loading }) {
  return (
    <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))', padding: '14px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'rgb(var(--color-text-muted))', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color ?? 'rgb(var(--color-text))' }}>{loading ? '—' : (value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: open ? '1px solid rgb(var(--color-border))' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>{title}</span>
          {badge}
        </div>
        <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function SortableTh({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th onClick={() => onSort(field)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label}{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );
}

function EmptyRow({ colSpan, message }) {
  return <tr><td colSpan={colSpan} style={{ padding: '24px 16px', textAlign: 'center', color: 'rgb(var(--color-text-muted))', fontSize: 13 }}>{message}</td></tr>;
}

function ComingSoonSection({ label }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'rgb(var(--color-surface-muted))', color: 'rgb(var(--color-text-muted))', border: '1px solid rgb(var(--color-border))' }}>S926+</span>
          <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{open ? '▲' : '▼'}</span>
        </div>
      </button>
      {open && (
        <div style={{ padding: '24px 16px', textAlign: 'center', borderTop: '1px solid rgb(var(--color-border))' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))', marginBottom: 4 }}>{label} — Coming Soon</div>
          <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>Backend endpoints live. Workspace UI in progress.</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// + New Program dropdown
// ---------------------------------------------------------------------------
function NewProgramDropdown({ onNewSpiff, onNewContest, onNewBonusPool, onNewNonCash, onNewClawback }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const items = [
    { label: 'New SPIFF Program',          onClick: () => { setOpen(false); onNewSpiff(); },      available: true },
    { label: 'New Contest',                onClick: () => { setOpen(false); onNewContest(); },    available: true },
    { label: 'New Bonus Pool',             onClick: () => { setOpen(false); onNewBonusPool(); },  available: true },
    { label: 'New Non-Cash Award Program', onClick: () => { setOpen(false); onNewNonCash(); },    available: true },
    { label: 'New Draw Authorization',     onClick: () => setOpen(false),                         available: false },
    { label: 'New Clawback Action',        onClick: () => { setOpen(false); onNewClawback(); },   available: true },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="primary-button" onClick={() => setOpen(o => !o)}>
        + New Program ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 50, background: 'rgb(var(--color-surface))', border: '1px solid rgb(var(--color-border))', borderRadius: 8, boxShadow: '0 4px 16px rgb(0 0 0 / 0.12)', minWidth: 220, overflow: 'hidden' }}>
          {items.map((item, i) => (
            <button key={item.label} type="button" onClick={item.available ? item.onClick : undefined}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', fontSize: 13, background: 'none', border: 'none', cursor: item.available ? 'pointer' : 'not-allowed', color: item.available ? 'rgb(var(--color-text))' : 'rgb(var(--color-text-muted))', borderBottom: i < items.length - 1 ? '1px solid rgb(var(--color-border))' : 'none' }}>
              {item.label}
              {!item.available && <span style={{ marginLeft: 6, fontSize: 10 }}>Soon</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// DRAW AUTHORIZATIONS SECTION
// Absorbed from AdjustmentsHub.jsx (S904) — logic unchanged.
// ===========================================================================
function DrawAuthorizationsSection({ period, planIdFilter, onPlanIdFilterChange }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage]       = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [summary, setSummary] = useState(null);
  const [items, setItems]     = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount]   = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');
  const [sortField, setSortField] = useState('fullName');
  const [sortDir, setSortDir]     = useState('asc');

  useEffect(() => { setPage(0); }, [planIdFilter, statusFilter, search]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
    if (planIdFilter != null)                   params.set('plan_id', planIdFilter);
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    if (period?.fiscal_period_id)               params.set('fiscal_period_id', period.fiscal_period_id);

    fetch(`${API_BASE}/execute/adjustments/draw-authorizations?${params}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (!active) return;
        setSummary(data.summary ?? null);
        setItems(data.drawAuthorizations ?? []);
        setTotalCount(data.totalCount ?? 0);
        setPageCount(data.pageCount ?? 1);
        setHasMore(data.hasMore ?? false);
      })
      .catch(e => { if (active) setError(e.message || 'Unable to load draw authorizations.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [planIdFilter, statusFilter, page, period?.fiscal_period_id, refreshKey]);

  function sortBy(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  const filtered = items
    .filter(i => !search || i.fullName?.toLowerCase().includes(search.toLowerCase()) || i.planCode?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  const pendingCount = summary?.pendingCount ?? 0;
  const startRow = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const endRow   = Math.min(page * PAGE_SIZE + PAGE_SIZE, totalCount);

  const pendingBadge = pendingCount > 0 ? (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgb(var(--color-warning, 202 138 4) / 0.12)', color: 'rgb(var(--color-warning, 202 138 4))' }}>
      {pendingCount} pending
    </span>
  ) : null;

  return (
    <CollapsibleSection title="Draw Authorizations" defaultOpen badge={pendingBadge}>
      {/* Summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, padding: '12px 16px', borderBottom: '1px solid rgb(var(--color-border))' }}>
        <SummaryCard label="Pending" value={summary?.pendingCount} color="rgb(var(--color-warning, 202 138 4))" loading={loading && !summary} />
        <SummaryCard label="Active"  value={summary?.activeCount}  color="rgb(var(--color-success, 22 163 74))" loading={loading && !summary} />
        <SummaryCard label="Revoked" value={summary?.revokedCount} color="rgb(var(--color-danger, 220 38 38))"  loading={loading && !summary} />
        <SummaryCard label="Total"   value={summary?.totalCount}   loading={loading && !summary} />
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid rgb(var(--color-border))' }}>
        <input type="text" placeholder="Search participants or plans…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }} />
        <input type="number" min="1" value={planIdFilter ?? ''} onChange={e => onPlanIdFilterChange(e.target.value ? Number(e.target.value) : null)}
          placeholder="Plan ID" style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, width: 90, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {[{ key: 'all', label: 'All' }, { key: 'pending', label: 'Pending' }, { key: 'active', label: 'Active' }, { key: 'revoked', label: 'Revoked' }].map(tab => (
            <button key={tab.key} type="button" onClick={() => setStatusFilter(tab.key)}
              style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20, border: '1px solid rgb(var(--color-border))', background: statusFilter === tab.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-surface))', color: statusFilter === tab.key ? '#fff' : 'rgb(var(--color-text-muted))', cursor: 'pointer' }}>
              {tab.label}
            </button>
          ))}
        </div>
        {(planIdFilter != null || statusFilter !== 'all') && (
          <button type="button" onClick={() => { onPlanIdFilterChange(null); setStatusFilter('all'); }}
            style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer' }}>
            ✕ Clear
          </button>
        )}
        <button type="button" onClick={() => setRefreshKey(k => k + 1)}
          style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Row count */}
      <div style={{ padding: '6px 16px', borderBottom: '1px solid rgb(var(--color-border))', fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
        {loading ? 'Loading…' : totalCount === 0 ? 'No results match the current filters'
          : `${startRow}–${endRow} of ${totalCount.toLocaleString()} authorization${totalCount !== 1 ? 's' : ''}`}
      </div>

      {error && <div style={{ padding: '12px 16px', color: 'rgb(var(--color-danger))', fontSize: 13 }} role="alert">{error}</div>}

      {/* Table — D-1 */}
      <div className="modifier-manager-table-wrapper">
        <table className="modifier-manager-table">
          <thead>
            <tr>
              <SortableTh label="Participant" field="fullName"            sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <SortableTh label="Plan"        field="planCode"            sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <SortableTh label="Balance"     field="openingBalance"      sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <SortableTh label="Method"      field="authorizationMethod" sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <SortableTh label="Status"      field="authorizationStatus" sortField={sortField} sortDir={sortDir} onSort={sortBy} />
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && <EmptyRow colSpan={5} message="Loading…" />}
            {!loading && filtered.length === 0 && (
              <EmptyRow colSpan={5} message={planIdFilter != null || statusFilter !== 'all' ? 'No draw authorizations match the current filters.' : 'No draw authorizations found for this period.'} />
            )}
            {filtered.map(item => {
              const cfg = AUTH_STATUS_CONFIG[item.authorizationStatus] ?? { label: item.authorizationStatus, color: 'rgb(var(--color-text-muted))' };
              return (
                <tr key={item.authId}>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{item.fullName}</div>
                    <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>Position {item.positionId?.slice(0, 8)}…</div>
                  </td>
                  <td>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{item.planCode}</div>
                    <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{item.planName}</div>
                  </td>
                  <td>
                    {item.openingBalance != null
                      ? <span style={{ fontSize: 13, fontWeight: 600 }}>{item.currencyCode} {parseFloat(item.openingBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      : <span style={{ color: 'rgb(var(--color-text-muted))' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{item.authorizationMethod ?? '—'}</td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cfg.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid rgb(var(--color-border))' }}>
          <button type="button" className="secondary-button" disabled={page === 0 || loading} onClick={() => setPage(p => Math.max(0, p - 1))}>← Previous</button>
          <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>Page {page + 1} of {pageCount}</span>
          <button type="button" className="secondary-button" disabled={!hasMore || loading} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </CollapsibleSection>
  );
}

// ===========================================================================
// SPIFF PROGRAMS SECTION
// ===========================================================================
function SpiffProgramsSection({ period, onOpenWorkspace }) {
  const [programs, setPrograms]         = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [refreshKey, setRefreshKey]     = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]             = useState('');
  const [sortField, setSortField]       = useState('created_at');
  const [sortDir, setSortDir]           = useState('desc');
  const [page, setPage]                 = useState(0);
  const PAGE = 50;

  useEffect(() => { setPage(0); }, [statusFilter, search, period?.fiscal_period_id]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    const params = new URLSearchParams({ include_superseded: 'false', limit: PAGE, offset: page * PAGE });
    if (period?.fiscal_period_id)               params.set('fiscal_period_id', period.fiscal_period_id);
    if (statusFilter && statusFilter !== 'all') params.set('plan_status', statusFilter);

    fetch(`${API_BASE}/spiff-plans?${params}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { if (!active) return; setPrograms(d.spiff_plans ?? []); setTotal(d.total ?? 0); })
      .catch(e => { if (active) setError(e.message || 'Unable to load SPIFF programs.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [statusFilter, page, period?.fiscal_period_id, refreshKey]);

  function sortBy(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  const pendingCount = programs.filter(p => p.plan_status === 'pending_approval').length;
  const hasMore = (page + 1) * PAGE < total;

  const filtered = programs
    .filter(p => !search || p.plan_name?.toLowerCase().includes(search.toLowerCase()) || p.spiff_plan_code?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  const pendingBadge = pendingCount > 0 ? (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgb(var(--color-warning, 202 138 4) / 0.12)', color: 'rgb(var(--color-warning, 202 138 4))' }}>
      {pendingCount} pending
    </span>
  ) : null;

  return (
    <CollapsibleSection title="SPIFF Programs" defaultOpen badge={pendingBadge}>
      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid rgb(var(--color-border))' }}>
        <input type="text" placeholder="Search programs…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {STATUS_FILTERS.map(f => (
            <button key={f.key} type="button" onClick={() => setStatusFilter(f.key)}
              style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20, border: '1px solid rgb(var(--color-border))', background: statusFilter === f.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-surface))', color: statusFilter === f.key ? '#fff' : 'rgb(var(--color-text-muted))', cursor: 'pointer' }}>
              {f.label}
            </button>
          ))}
        </div>
        <button type="button" className="primary-button" onClick={() => onOpenWorkspace(null)} style={{ marginLeft: 'auto' }}>+ New SPIFF Program</button>
        <button type="button" onClick={() => setRefreshKey(k => k + 1)}
          style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Row count */}
      <div style={{ padding: '6px 16px', borderBottom: '1px solid rgb(var(--color-border))', fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
        {loading ? 'Loading…' : `${filtered.length} of ${total.toLocaleString()} program${total !== 1 ? 's' : ''}`}
      </div>

      {error && <div style={{ padding: '12px 16px', color: 'rgb(var(--color-danger))', fontSize: 13 }}>{error}</div>}

      {/* Table — D-1: scrollable, sortable, searchable, clickable */}
      <div className="modifier-manager-table-wrapper">
        <table className="modifier-manager-table">
          <thead>
            <tr>
              <SortableTh label="Program name"   field="plan_name"      sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <SortableTh label="Code"           field="spiff_plan_code" sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <SortableTh label="Trigger"        field="trigger_model"  sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <SortableTh label="Effective from" field="effective_from" sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <SortableTh label="Status"         field="plan_status"    sortField={sortField} sortDir={sortDir} onSort={sortBy} />
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && <EmptyRow colSpan={6} message="Loading…" />}
            {!loading && filtered.length === 0 && (
              <EmptyRow colSpan={6} message={statusFilter !== 'all' || search ? 'No programs match the current filters.' : 'No SPIFF programs yet. Create your first program above.'} />
            )}
            {filtered.map(p => {
              const badge = SPIFF_STATUS_BADGE[p.plan_status] ?? { cls: 'modifier-manager-badge--not-created', label: p.plan_status };
              const triggerLabel = p.trigger_model === 'transaction_triggered' ? 'Transaction' : p.trigger_model === 'manager_assigned' ? 'Manager' : p.trigger_model ?? '—';
              return (
                <tr key={p.plan_id} onClick={() => onOpenWorkspace(p.plan_id)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>{p.plan_name}</div>
                    {p.plan_description && <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{p.plan_description}</div>}
                  </td>
                  <td>
                    {p.spiff_plan_code
                      ? <code style={{ fontSize: 11, fontFamily: 'ui-monospace,monospace', background: 'rgb(var(--color-surface-muted))', padding: '1px 5px', borderRadius: 3, border: '1px solid rgb(var(--color-border))' }}>{p.spiff_plan_code}</code>
                      : <span style={{ color: 'rgb(var(--color-text-muted))' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{triggerLabel}</td>
                  <td style={{ fontSize: 12 }}>{p.effective_from ?? '—'}</td>
                  <td><span className={`modifier-manager-badge ${badge.cls}`}>{badge.label}</span></td>
                  <td>
                    <button type="button" className="text-button"
                      onClick={e => { e.stopPropagation(); onOpenWorkspace(p.plan_id); }}
                      aria-label={`Open ${p.plan_name}`}>→</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid rgb(var(--color-border))' }}>
          <button type="button" className="secondary-button" disabled={page === 0 || loading} onClick={() => setPage(p => p - 1)}>← Previous</button>
          <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>Page {page + 1}</span>
          <button type="button" className="secondary-button" disabled={!hasMore || loading} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </CollapsibleSection>
  );
}

// ===========================================================================
// CONTESTS SECTION — S925 C-67
// ===========================================================================
function ContestsSection({ period, onOpenWorkspace }) {
  const [contests, setContests]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch]         = useState('');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    const params = new URLSearchParams({ limit: '200' });
    if (period?.fiscal_period_id) params.set('fiscal_period_id', String(period.fiscal_period_id));

    fetch(`${API_BASE}/contests?${params}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { if (active) setContests(d.items ?? []); })
      .catch(e => { if (active) setError(e.message || 'Unable to load contests.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [period?.fiscal_period_id, refreshKey]);

  const filtered = contests.filter(c =>
    !search ||
    c.contest_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.contest_code?.toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount = contests.filter(c => c.contest_status === 'open' && !c.approved_by).length;
  const pendingBadge = pendingCount > 0 ? (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgb(var(--color-warning, 202 138 4) / 0.12)', color: 'rgb(var(--color-warning, 202 138 4))' }}>
      {pendingCount} pending approval
    </span>
  ) : null;

  return (
    <CollapsibleSection title="Contests" defaultOpen badge={pendingBadge}>
      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid rgb(var(--color-border))' }}>
        <input type="text" placeholder="Search contests…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }} />
        <button type="button" className="primary-button" onClick={() => onOpenWorkspace(null)} style={{ marginLeft: 'auto' }}>
          + New Contest
        </button>
        <button type="button" onClick={() => setRefreshKey(k => k + 1)}
          style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Row count */}
      <div style={{ padding: '6px 16px', borderBottom: '1px solid rgb(var(--color-border))', fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
        {loading ? 'Loading…' : `${filtered.length} of ${contests.length} contest${contests.length !== 1 ? 's' : ''}`}
      </div>

      {error && <div style={{ padding: '12px 16px', color: 'rgb(var(--color-danger))', fontSize: 13 }} role="alert">{error}</div>}

      {/* Table — D-1 */}
      <div className="modifier-manager-table-wrapper">
        <table className="modifier-manager-table">
          <thead>
            <tr>
              <th>Contest</th>
              <th>Date range</th>
              <th>Rank metric</th>
              <th>Eligibility</th>
              <th>Status</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && <EmptyRow colSpan={6} message="Loading…" />}
            {!loading && filtered.length === 0 && (
              <EmptyRow colSpan={6} message={search ? 'No contests match the search.' : 'No contests yet. Create your first contest above.'} />
            )}
            {filtered.map(c => (
              <tr key={c.contest_id} onClick={() => onOpenWorkspace(c.contest_id)} style={{ cursor: 'pointer' }}>
                <td>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>{c.contest_name}</div>
                  {c.contest_code && (
                    <code style={{ fontSize: 11, fontFamily: 'ui-monospace,monospace', background: 'rgb(var(--color-surface-muted))', padding: '1px 5px', borderRadius: 3, border: '1px solid rgb(var(--color-border))' }}>
                      {c.contest_code}
                    </code>
                  )}
                </td>
                <td style={{ fontSize: 12 }}>
                  {c.start_date ? new Date(c.start_date).toLocaleDateString() : '—'} → {c.end_date ? new Date(c.end_date).toLocaleDateString() : '—'}
                </td>
                <td style={{ fontSize: 12 }}>{CONTEST_RANK_METRIC_LABELS[c.rank_metric] ?? c.rank_metric ?? '—'}</td>
                <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{(c.eligibility_type ?? '—').replace('_', ' ')}</td>
                <td>
                  <span className={`modifier-manager-badge ${CONTEST_BADGE[c.contest_status] ?? ''}`}>
                    {CONTEST_STATUS_LABEL[c.contest_status] ?? c.contest_status}
                  </span>
                </td>
                <td>
                  <button type="button" className="text-button"
                    onClick={e => { e.stopPropagation(); onOpenWorkspace(c.contest_id); }}
                    aria-label={`Open ${c.contest_name}`}>→</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}

// ===========================================================================
// BONUS POOLS SECTION — S926 C-68
// Entry point into BonusPoolWorkspace. No list preview here — workspace
// owns all pool data. Simple CTA section consistent with hub pattern.
// ===========================================================================
function BonusPoolsSection({ onOpenWorkspace }) {
  return (
    <CollapsibleSection title="Bonus Pools" defaultOpen
      badge={<span className="modifier-manager-badge modifier-manager-badge--readonly">Enterprise</span>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
        <div style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))' }}>
          Discretionary pool allocation programs. Two-tier approval: pool approval + per-allocation approval.
          Scoped at tenant + fiscal period — no plan component required (AD-119).
        </div>
        <div>
          <button type="button" className="primary-button" onClick={onOpenWorkspace}>
            Manage Bonus Pools →
          </button>
        </div>
      </div>
    </CollapsibleSection>
  );
}

// ===========================================================================
// NON-CASH AWARDS SECTION — S926 C-69
// Entry point into NonCashWorkspace. Simple CTA section consistent with hub.
// ===========================================================================
function NonCashSection({ onOpenWorkspace }) {
  return (
    <CollapsibleSection title="Non-Cash Awards" defaultOpen
      badge={<span className="modifier-manager-badge modifier-manager-badge--complete">Standard</span>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
        <div style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))' }}>
          Recognition programs — points, merchandise, travel, gift cards.
          Manage the award type catalog and per-recipient grants (manager_spot + SPIFF sources).
        </div>
        <div>
          <button type="button" className="primary-button" onClick={onOpenWorkspace}>
            Manage Non-Cash Awards →
          </button>
        </div>
      </div>
    </CollapsibleSection>
  );
}

// ===========================================================================
// CLAWBACK ACTIONS SECTION — S932 C-70
// Entry point into ClawbackWorkspace. Simple CTA section per hub pattern.
// RBAC: manager sees Results tab only (enforced inside ClawbackWorkspace).
// comp_admin + finance see both tabs.
// ===========================================================================
function ClawbackSection({ onOpenWorkspace }) {
  return (
    <CollapsibleSection title="Clawback Actions" defaultOpen
      badge={<span className="modifier-manager-badge modifier-manager-badge--readonly">Standard</span>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
        <div style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))' }}>
          Churn event ingestion and clawback results. Policies are configured at design time
          in the plan&apos;s Adjustments section.
        </div>
        <div>
          <button type="button" className="primary-button" onClick={onOpenWorkspace}>
            Manage Clawback Actions →
          </button>
        </div>
      </div>
    </CollapsibleSection>
  );
}

// ===========================================================================
// MAIN — ProgramsAndAdjustmentsHub (C-65)
// ===========================================================================
export default function ProgramsAndAdjustmentsHub({ period, currentUser }) {
  const location = useLocation();
  const navigate  = useNavigate();

  const searchParams    = new URLSearchParams(location.search);
  const deepLinkPlanId  = searchParams.get('planId')      ? Number(searchParams.get('planId'))      : null;
  const deepLinkSpiffId = searchParams.get('spiffPlanId') ? Number(searchParams.get('spiffPlanId')) : null;

  const [instrumentFilter, setInstrumentFilter] = useState('all');
  const [drawPlanIdFilter, setDrawPlanIdFilter] = useState(deepLinkPlanId);

  // SPIFF workspace state
  const [activeSpiffPlanId, setActiveSpiffPlanId] = useState(deepLinkSpiffId);
  const [showSpiffWorkspace, setShowSpiffWorkspace] = useState(!!deepLinkSpiffId);

  // Contest workspace state — S925
  const [activeContestId, setActiveContestId] = useState(null);
  const [showContestWorkspace, setShowContestWorkspace] = useState(false);

  // BonusPool workspace state — S926
  const [showBonusPoolWorkspace, setShowBonusPoolWorkspace] = useState(false);

  // NonCash workspace state — S926
  const [showNonCashWorkspace, setShowNonCashWorkspace] = useState(false);

  // Clawback workspace state — S932
  const [showClawbackWorkspace, setShowClawbackWorkspace] = useState(false);

  const openSpiffWorkspace = useCallback((planId) => {
    setActiveSpiffPlanId(planId ?? null);
    setShowSpiffWorkspace(true);
    const next = new URLSearchParams(location.search);
    if (planId) next.set('spiffPlanId', planId);
    else next.delete('spiffPlanId');
    navigate(`/admin/execute/adjustments?${next}`, { replace: true });
  }, [location.search, navigate]);

  const closeSpiffWorkspace = useCallback(() => {
    setShowSpiffWorkspace(false);
    setActiveSpiffPlanId(null);
    const next = new URLSearchParams(location.search);
    next.delete('spiffPlanId');
    navigate(`/admin/execute/adjustments?${next}`, { replace: true });
  }, [location.search, navigate]);

  const openContestWorkspace = useCallback((contestId) => {
    setActiveContestId(contestId ?? null);
    setShowContestWorkspace(true);
  }, []);

  const closeContestWorkspace = useCallback(() => {
    setShowContestWorkspace(false);
    setActiveContestId(null);
  }, []);

  const openBonusPoolWorkspace = useCallback(() => {
    setShowBonusPoolWorkspace(true);
  }, []);

  const closeBonusPoolWorkspace = useCallback(() => {
    setShowBonusPoolWorkspace(false);
  }, []);

  const openNonCashWorkspace = useCallback(() => {
    setShowNonCashWorkspace(true);
  }, []);

  const closeNonCashWorkspace = useCallback(() => {
    setShowNonCashWorkspace(false);
  }, []);

  const openClawbackWorkspace = useCallback(() => {
    setShowClawbackWorkspace(true);
  }, []);

  const closeClawbackWorkspace = useCallback(() => {
    setShowClawbackWorkspace(false);
  }, []);

  const handleDrawPlanIdFilter = useCallback((id) => {
    setDrawPlanIdFilter(id);
    if (id == null) {
      const next = new URLSearchParams(location.search);
      next.delete('planId');
      navigate(`/admin/execute/adjustments?${next}`, { replace: true });
    }
  }, [location.search, navigate]);

  // SPIFF workspace overlay
  if (showSpiffWorkspace) {
    return (
      <div style={{ fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>
        <SpiffProgramWorkspace
          planId={activeSpiffPlanId}
          fiscalPeriodId={period?.fiscal_period_id}
          currentUser={currentUser}
          onClose={closeSpiffWorkspace}
          onSaved={() => {}}
        />
      </div>
    );
  }

  // Contest workspace overlay — S925
  if (showContestWorkspace) {
    return (
      <div style={{ fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>
        <ContestWorkspace
          contestId={activeContestId}
          fiscalPeriodId={period?.fiscal_period_id}
          currentUser={currentUser}
          onClose={closeContestWorkspace}
          onSaved={() => {}}
        />
      </div>
    );
  }

  // BonusPool workspace overlay — S926
  if (showBonusPoolWorkspace) {
    return (
      <div style={{ fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>
        <BonusPoolWorkspace
          fiscalPeriodId={period?.fiscal_period_id}
          currentUser={currentUser}
          onClose={closeBonusPoolWorkspace}
          onSaved={() => {}}
        />
      </div>
    );
  }

  // NonCash workspace overlay — S926
  if (showNonCashWorkspace) {
    return (
      <div style={{ fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>
        <NonCashWorkspace
          fiscalPeriodId={period?.fiscal_period_id}
          currentUser={currentUser}
          onClose={closeNonCashWorkspace}
          onSaved={() => {}}
        />
      </div>
    );
  }

  // Clawback workspace overlay — S932 C-70
  if (showClawbackWorkspace) {
    return (
      <div style={{ fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>
        <ClawbackWorkspace
          fiscalPeriodId={period?.fiscal_period_id}
          currentUser={currentUser}
          onClose={closeClawbackWorkspace}
        />
      </div>
    );
  }

  const showDraw      = instrumentFilter === 'all' || instrumentFilter === 'draw';
  const showSpiff     = instrumentFilter === 'all' || instrumentFilter === 'spiff';
  const showContest   = instrumentFilter === 'all' || instrumentFilter === 'contest';
  const showBonusPool = instrumentFilter === 'all' || instrumentFilter === 'bonus_pool';
  const showNonCash   = instrumentFilter === 'all' || instrumentFilter === 'non_cash';
  const showClawback  = instrumentFilter === 'all' || instrumentFilter === 'clawback';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>

      {/* Page header */}
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))', margin: '0 0 4px' }}>
          EXECUTE · Programs &amp; Adjustments
        </p>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'rgb(var(--color-text))', margin: '0 0 4px' }}>Programs &amp; Adjustments</h1>
            <p style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))', margin: 0 }}>
              Cross-plan operational work queue — manage all runtime adjustment programs.
              {period?.period_label && <> · Period: <strong>{period.period_label}</strong></>}
            </p>
          </div>
          <NewProgramDropdown
            onNewSpiff={() => openSpiffWorkspace(null)}
            onNewContest={() => openContestWorkspace(null)}
            onNewBonusPool={openBonusPoolWorkspace}
            onNewNonCash={openNonCashWorkspace}
            onNewClawback={openClawbackWorkspace}
          />
        </div>
      </div>

      {/* Deep-link banner */}
      {drawPlanIdFilter != null && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgb(var(--color-primary) / 0.07)', border: '1px solid rgb(var(--color-primary) / 0.2)', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Filtered to Plan ID <strong>{drawPlanIdFilter}</strong></span>
          <button type="button" onClick={() => handleDrawPlanIdFilter(null)}
            style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-text-muted))' }}>Show all plans ✕</button>
        </div>
      )}

      {/* Instrument type filter bar */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {INSTRUMENT_TYPES.map(t => (
          <button key={t.key} type="button" onClick={() => setInstrumentFilter(t.key)}
            style={{ fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, border: '1px solid rgb(var(--color-border))', background: instrumentFilter === t.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-surface))', color: instrumentFilter === t.key ? '#fff' : 'rgb(var(--color-text-muted))', cursor: 'pointer', transition: 'background 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Sections */}
      {showDraw      && <DrawAuthorizationsSection period={period} planIdFilter={drawPlanIdFilter} onPlanIdFilterChange={handleDrawPlanIdFilter} />}
      {showSpiff     && <SpiffProgramsSection period={period} onOpenWorkspace={openSpiffWorkspace} />}
      {showContest   && <ContestsSection period={period} onOpenWorkspace={openContestWorkspace} />}
      {showBonusPool && <BonusPoolsSection onOpenWorkspace={openBonusPoolWorkspace} />}
      {showNonCash   && <NonCashSection onOpenWorkspace={openNonCashWorkspace} />}
      {showClawback  && <ClawbackSection onOpenWorkspace={openClawbackWorkspace} />}

      {/* Architecture note */}
      <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', padding: '0 2px' }}>
        CA-2 approval for design-time plan artifacts remains in DESIGN → Approvals.
        This hub surfaces operational runtime instrument state only. AD-76 S901.
      </div>
    </div>
  );
}
